import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { syncUserPlanToMixpanel } from '@/lib/mixpanel-user-sync';

// ============================================================================
// CENTRALIZED TYPES
// ============================================================================

export interface AddOnSubscription {
  id: string;
  status: string;
  priceId: string;
  trialEnd: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  createdAt: Date;
}

export interface SubscriptionStatus {
  isSubscribed: boolean;
  isTrialing: boolean;
  subscriptionStatus: 'none' | 'trialing' | 'active' | 'canceled' | 'past_due';
  trialEndsAt: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  primarySubscriptionId: string | null;
  addOnSubscriptions: AddOnSubscription[];
  loading: boolean;
  error: string | null;
}

export interface TrialInfo {
  isActive: boolean;
  isExpired: boolean;
  daysRemaining: number;
  progressPercentage: number;
  isUrgent: boolean; // <= 7 days remaining
}

export interface SubscriptionTier {
  name: string;
  displayName: string;
  isPro: boolean;
  isTrial: boolean;
  isPaid: boolean;
}

export interface SubscriptionDisplayRules {
  shouldShowTrialComponents: boolean;
  shouldShowUpgradePrompts: boolean;
  shouldShowSubscriptionManagement: boolean;
  shouldShowBillingInfo: boolean;
}

// Legacy interface for backward compatibility
interface LegacyBillingSubscription {
  isSubscribed: boolean;
  isTrialing: boolean;
  subscriptionStatus: string | null;
  trialEndsAt: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// BUSINESS LOGIC UTILITIES
// ============================================================================

/**
 * Calculate trial information with dynamic progress calculation
 * Supports both 30-day and 180-day (6-month) trials
 * 
 * @param trialEndsAt - When the trial ends
 * @param trialStartedAt - When the trial started (optional, for accurate progress)
 */
export function getTrialInfo(trialEndsAt: Date | null, trialStartedAt?: Date | null): TrialInfo {
  if (!trialEndsAt) {
    return {
      isActive: false,
      isExpired: true,
      daysRemaining: 0,
      progressPercentage: 100,
      isUrgent: false,
    };
  }

  const now = new Date();
  const diffTime = trialEndsAt.getTime() - now.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Calculate progress percentage dynamically based on trial length
  let progressPercentage: number;
  
  if (trialStartedAt) {
    // If we have start date, calculate exact progress
    const totalTrialDays = Math.ceil((trialEndsAt.getTime() - trialStartedAt.getTime()) / (1000 * 60 * 60 * 24));
    const daysElapsed = totalTrialDays - daysRemaining;
    progressPercentage = Math.max(0, Math.min(100, (daysElapsed / totalTrialDays) * 100));
  } else {
    // Infer trial length from days remaining
    // If > 30 days remaining, it's likely a 180-day trial (6 months)
    // If <= 30 days remaining, use 30-day baseline
    const inferredTrialLength = daysRemaining > 30 ? 180 : 30;
    progressPercentage = Math.max(0, Math.min(100, ((inferredTrialLength - daysRemaining) / inferredTrialLength) * 100));
  }

  return {
    isActive: daysRemaining > 0,
    isExpired: daysRemaining <= 0,
    daysRemaining: Math.max(0, daysRemaining),
    progressPercentage,
    isUrgent: daysRemaining <= 7 && daysRemaining > 0,
  };
}

export function getSubscriptionTier(status: SubscriptionStatus): SubscriptionTier {
  // Paid subscribers are ALWAYS "Pro" regardless of trial status
  // This handles both fully paid subscriptions and paid subscriptions with Stripe trial periods
  if (status.isSubscribed) {
    return {
      name: 'pro',
      displayName: 'ChronoCal Pro',
      isPro: true,
      isTrial: false,
      isPaid: true,
    };
  }

  // Free trial (not subscribed but trialing) - this is the pre-payment trial
  if (status.isTrialing && !status.isSubscribed) {
    return {
      name: 'free-trial',
      displayName: 'Free Trial',
      isPro: false,
      isTrial: true,
      isPaid: false,
    };
  }

  // No subscription, no trial
  return {
    name: 'free',
    displayName: 'Free',
    isPro: false,
    isTrial: false,
    isPaid: false,
  };
}

export function getSubscriptionDisplayRules(status: SubscriptionStatus): SubscriptionDisplayRules {
  return {
    // Show trial components for free trial users only (active trials)
    shouldShowTrialComponents: status.isTrialing && !status.isSubscribed,
    
    // Show upgrade prompts for non-subscribed users (both active and expired trials)
    shouldShowUpgradePrompts: !status.isSubscribed,
    
    // Show subscription management for paid subscribers (including those in trial)
    shouldShowSubscriptionManagement: status.isSubscribed,
    
    // Show billing info for paid subscribers only (not free trial users)
    shouldShowBillingInfo: status.isSubscribed && !status.isTrialing,
  };
}

export function useBillingSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    isSubscribed: false,
    isTrialing: false,
    subscriptionStatus: 'none',
    trialEndsAt: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    loading: true,
    error: null,
  });

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Use the database function to get subscription status
      const { data, error } = await supabase
        .rpc('get_user_subscription_status', { p_user_id: user.id })
        .single();

      if (error) {
        // If function returns no data, user has no subscription (free tier)
        if (error.code === 'PGRST116') {
          setSubscription({
            isSubscribed: false,
            isTrialing: false,
            subscriptionStatus: 'none',
            trialEndsAt: null,
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false,
            loading: false,
            error: null,
          });
          return;
        }

        console.error('Error fetching subscription:', error);
        setSubscription(prev => ({
          ...prev,
          loading: false,
          error: error.message,
        }));
        return;
      }

      // Parse add-on subscriptions
      const addOnSubscriptions: AddOnSubscription[] = data?.add_on_subscriptions 
        ? (Array.isArray(data.add_on_subscriptions) ? data.add_on_subscriptions : [])
            .map((addon: any) => ({
              id: addon.id,
              status: addon.status,
              priceId: addon.price_id,
              trialEnd: addon.trial_end ? new Date(addon.trial_end) : null,
              currentPeriodEnd: addon.current_period_end ? new Date(addon.current_period_end) : null,
              cancelAtPeriodEnd: addon.cancel_at_period_end || false,
              createdAt: new Date(addon.created_at),
            }))
        : [];

      // Update state with subscription data
      setSubscription({
        isSubscribed: data?.is_subscribed || false,
        isTrialing: data?.is_trialing || false,
        subscriptionStatus: (data?.subscription_status as 'none' | 'trialing' | 'active' | 'canceled' | 'past_due') || 'none',
        trialEndsAt: data?.trial_ends_at ? new Date(data.trial_ends_at) : null,
        currentPeriodEnd: data?.current_period_end ? new Date(data.current_period_end) : null,
        cancelAtPeriodEnd: data?.cancel_at_period_end || false,
        primarySubscriptionId: data?.primary_subscription_id || null,
        addOnSubscriptions,
        loading: false,
        error: null,
      });

      // Sync subscription status to Mixpanel whenever it changes
      if (user?.id) {
        syncUserPlanToMixpanel(user.id);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();

    // Only subscribe to subscription changes if user exists
    if (!user) {
      return;
    }

    // Subscribe to subscription changes
    const channel = supabase
      .channel('billing_subscription_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'billing_subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refetch on any changes
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscription]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const trialInfo = getTrialInfo(subscription.trialEndsAt);
  const subscriptionTier = getSubscriptionTier(subscription);
  const displayRules = getSubscriptionDisplayRules(subscription);

  // ============================================================================
  // RETURN ENHANCED INTERFACE
  // ============================================================================

  return {
    // Core subscription data (backward compatible)
    ...subscription,
    
    // Computed trial information
    trialInfo,
    
    // Computed subscription tier
    subscriptionTier,
    
    // Display rules for components
    displayRules,
    
    // Utility functions
    refreshSubscription: fetchSubscription,
  };
}

// ============================================================================
// ADD-ON UTILITIES
// ============================================================================

export function hasAddOn(subscription: SubscriptionStatus, priceId: string): boolean {
  return subscription.addOnSubscriptions.some(addon => 
    addon.priceId === priceId && 
    ['active', 'trialing'].includes(addon.status)
  );
}

export function getActiveAddOns(subscription: SubscriptionStatus): AddOnSubscription[] {
  return subscription.addOnSubscriptions.filter(addon => 
    ['active', 'trialing'].includes(addon.status)
  );
}

export function getAddOnByPriceId(subscription: SubscriptionStatus, priceId: string): AddOnSubscription | null {
  return subscription.addOnSubscriptions.find(addon => addon.priceId === priceId) || null;
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

// Legacy helper function
export function getTrialDaysRemaining(trialEndsAt: Date | null): number {
  return getTrialInfo(trialEndsAt).daysRemaining;
}
